import React, { createContext, useMemo, useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Global from '../../../store/global-selectors';
import { BuildFulfillmentPlanForMAS } from '../helper';
import { MARK_AS_SHIPPED_CARRIER_ID } from '../constants';
import { useProviderServices } from '../../../hooks/use-provider-services';
import { MarketplaceIds } from '../../../enums/marketplace-ids';

const marketplaceRequiresShipFromWhitelist = new Set([MarketplaceIds.WalmartCA, MarketplaceIds.Wish]);

export const MarkAsShippedContext = createContext({});

export const Provider = ({ fulfillmentPlanIds: fulfillmentPlanIdsProps, children }) => {
  const [fulfillmentPlanIds, setFulfillmentPlanIds] = useState(fulfillmentPlanIdsProps);
  const entities = useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds }));
  const salesOrders = useSelector((state) =>
    Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
      fulfillmentPlanIds,
    }),
  );
  const services = useProviderServices();
  const initialFulfillmentPlans = entities.reduce((acc, entity) => {
    const { orderNumber, shipTos } = salesOrders.find((so) => so.salesOrderId === entity.salesOrdersFulfilledIds[0]);
    const { serviceId, restrictions } = entity;
    const selectedService = services.find((service) => service.serviceId === serviceId);
    const carrierId = selectedService?.carrierId;

    return {
      ...acc,
      [entity.fulfillmentPlanId]: {
        carrierId,
        ...BuildFulfillmentPlanForMAS(orderNumber, restrictions, shipTos),
      },
    };
  }, {});

  const [fulfillmentPlans, setFulfillmentPlans] = useState(initialFulfillmentPlans);
  const [selectedFulfillmentPlanIds, setSelectedFulfillmentPlanIds] = useState(fulfillmentPlanIds);
  const [hasError, setHasError] = useState(false);

  const bulkRemoveFulfillmentPlans = useCallback(() => {
    const newFulfillmentPlanIds = fulfillmentPlanIds.filter((fulfillmentPlanId) => !selectedFulfillmentPlanIds.includes(fulfillmentPlanId));

    setFulfillmentPlanIds(newFulfillmentPlanIds);
  }, [fulfillmentPlanIds, selectedFulfillmentPlanIds, setFulfillmentPlanIds]);

  const updateFulfillmentPlan = useCallback((fulfillmentPlanId, property, value) => {
    setFulfillmentPlans((fps) => {
      const fulfillmentPlan = fps[fulfillmentPlanId];
      return {
        ...fps,
        [fulfillmentPlanId]: {
          ...fulfillmentPlan,
          [property]: value,
        },
      };
    });
  }, []);

  const bulkUpdateFulfillmentPlans = useCallback(
    (property, value) => {
      setFulfillmentPlans((fps) =>
        fulfillmentPlanIds.reduce((acc, fulfillmentPlanId) => {
          const currentPlan = fps[fulfillmentPlanId];
          const ignoreUpdate =
            !selectedFulfillmentPlanIds.includes(fulfillmentPlanId) ||
            (property === MARK_AS_SHIPPED_CARRIER_ID && currentPlan.disableCarrierDropdown === true);
          const fpToAdd = ignoreUpdate ? currentPlan : { ...currentPlan, [property]: value };
          return {
            ...acc,
            [fulfillmentPlanId]: fpToAdd,
          };
        }, {}),
      );
    },
    [fulfillmentPlanIds, selectedFulfillmentPlanIds],
  );

  const shouldForceShipFrom = salesOrders.some((order) => marketplaceRequiresShipFromWhitelist.has(Number(order?.store?.marketplaceId)));

  const setAndSelectFulfillmentPlanIds = (fpIds) => {
    setSelectedFulfillmentPlanIds(fpIds);
    setFulfillmentPlanIds(fpIds);
  };

  const value = useMemo(
    () => ({
      fulfillmentPlanIds,
      fulfillmentPlans,
      updateFulfillmentPlan,
      bulkUpdateFulfillmentPlans,
      salesOrders,
      selectedFulfillmentPlanIds,
      setSelectedFulfillmentPlanIds,
      bulkRemoveFulfillmentPlans,
      setHasError,
      hasError,
      setFulfillmentPlans,
      setAndSelectFulfillmentPlanIds,
      shouldForceShipFrom,
    }),
    [
      fulfillmentPlanIds,
      fulfillmentPlans,
      updateFulfillmentPlan,
      bulkUpdateFulfillmentPlans,
      salesOrders,
      selectedFulfillmentPlanIds,
      bulkRemoveFulfillmentPlans,
      hasError,
      shouldForceShipFrom,
    ],
  );

  return <MarkAsShippedContext.Provider value={value}>{children}</MarkAsShippedContext.Provider>;
};

Provider.propTypes = {
  fulfillmentPlanIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default {
  MarkAsShippedContext,
  Provider,
};
